import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, type Location } from 'react-router-dom';
import { useAuthStore, useUserPreferencesStore, FONT_FAMILIES, FONT_SIZES, ThemeMode } from './store';
import { useSidebarStore } from './store/ui';
import { HomePage, WorldPage, LoginPage, RegisterPage, AccountPage, VerifyEmailPage, OnboardingPage } from './pages';
import { AdminDashboard, AdminUsers, AdminWorlds, AdminInvitations, AdminUsage, AdminModelStats, AdminInference, AdminTraces, AdminScaffolding, AdminEval } from './pages/admin';
import { ToastContainer } from './components/Toast';
import { SettingsModal } from './components';
import { VersionBadge } from './components/VersionBadge';
import { AccountModal } from './pages/AccountPage';

function App() {
  const checkAuth = useAuthStore(state => state.checkAuth);
  const isLoading = useAuthStore(state => state.isLoading);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const location = useLocation();
  const state = location.state as { backgroundLocation?: Location } | null;
  
  const { theme, fontFamily, fontSize } = useUserPreferencesStore();
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    const media = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = (mode: ThemeMode, prefersDark: boolean) => {
      const isDark = mode === 'dark' || (mode === 'system' && prefersDark);
      root.classList.toggle('dark-mode', isDark);
      root.classList.toggle('light-mode', !isDark);
    };

    const handleChange = (event: MediaQueryListEvent) => {
      if (theme === 'system') {
        applyTheme('system', event.matches);
      }
    };

    applyTheme(theme, media.matches);

    if (theme === 'system') {
      if (media.addEventListener) {
        media.addEventListener('change', handleChange);
        return () => media.removeEventListener('change', handleChange);
      }
      media.addListener(handleChange);
      return () => media.removeListener(handleChange);
    }
    return undefined;
  }, [theme]);

  // Apply font preferences as CSS variables
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--font-family', FONT_FAMILIES[fontFamily]);
    root.style.setProperty('--font-size', FONT_SIZES[fontSize]);
  }, [fontFamily, fontSize]);

  // Global keyboard shortcut: `[` toggles the sidebar. Bound at App
  // level so it works from any route. Skipped while typing in input/
  // textarea/contenteditable so users can type bracket characters
  // freely.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== '[') return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) return;
      }
      e.preventDefault();
      useSidebarStore.getState().toggleSidebar();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="text-center">
          <div className="spinner w-10 h-10 mx-auto mb-4" />
          <p className="text-dark-200">Loading Textborne...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes location={state?.backgroundLocation || location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/world/:worldId" element={<WorldPage />} />
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/account" element={<AccountPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Admin Routes */}
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/worlds" element={<AdminWorlds />} />
        <Route path="/admin/worlds/:worldId" element={<AdminWorlds />} />
        <Route path="/admin/invitations" element={<AdminInvitations />} />
        <Route path="/admin/usage" element={<AdminUsage />} />
        <Route path="/admin/model-stats" element={<AdminModelStats />} />
        <Route path="/admin/inference" element={<AdminInference />} />
        <Route path="/admin/inference/:logId" element={<AdminInference />} />
        <Route path="/admin/worlds/:worldId/traces" element={<AdminTraces />} />
        <Route path="/admin/worlds/:worldId/traces/:traceId" element={<AdminTraces />} />
        <Route path="/admin/scaffolding" element={<AdminScaffolding />} />
        <Route path="/admin/scaffolding/:traceId" element={<AdminScaffolding />} />
        <Route path="/admin/eval" element={<AdminEval />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {state?.backgroundLocation && (
        <Routes>
          <Route path="/account" element={<AccountModal />} />
        </Routes>
      )}
      
      {/* Global toast notifications */}
      <ToastContainer />
      
      {/* Settings modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />

      <VersionBadge />
    </>
  );
}

export default App;
